import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import {
  PanelProceso,
  type EstadoProceso,
  type ProcesoAnterior,
} from '@intelishs-group/ui';
import { BulkRegistrosComponent } from '@components/bulk-registros/bulk-registros';
import {
  ComplementosBulkTrackingService,
  cerradosDe,
  type EstadoTanda,
} from '@core/complementos-bulk-tracking.service';

const ESTADO: Record<EstadoTanda, EstadoProceso> = {
  siguiendo: 'en-proceso',
  terminado: 'terminado',
  timeout: 'sin-confirmar',
};

/**
 * Seguimiento de la tanda de complementos de pago masivos: el
 * `<intelishs-panel-proceso>` de `@intelishs-group/ui` (≥ 5.2.12) alimentado por
 * el {@link ComplementosBulkTrackingService}, con el detalle por renglón
 * —{@link BulkRegistrosComponent}, que es de facturación— en su hueco.
 *
 * Se monta en dos sitios a la vez, el drawer del header y la página de Cargas,
 * y los dos muestran lo mismo con un solo sondeo: no recibe nada por `input()`,
 * lo lee del servicio.
 *
 * `display: contents` en el host: dentro del drawer, el panel reparte el alto
 * contra su contenedor (`max-height: 100%`, y sólo el detalle cede). Con este
 * host como caja intermedia, ese 100 % se resolvería contra él —un elemento sin
 * alto— y el detalle dejaría de hacer scroll.
 */
@Component({
  selector: 'app-seguimiento-tanda',
  imports: [PanelProceso, BulkRegistrosComponent],
  template: `
    <intelishs-panel-proceso
      [estado]="estado()"
      [fecha]="tanda()?.iniciadaEn"
      [hechos]="hechos()"
      [total]="tanda()?.enviados ?? 0"
      [conteos]="tanda()?.resumen"
      [aviso]="tanda()?.aviso"
      [error]="tracking.errorRegistros()"
      [cargando]="tracking.cargandoRegistros()"
      [anteriores]="anteriores()"
      tituloAnteriores="Tandas anteriores"
      textoVacio="No hay tandas de complementos que seguir."
      (seleccionar)="tracking.seleccionar($event)"
    >
      @if (tracking.registrosSeleccionados().length) {
        <intelishs-bulk-registros
          [registros]="tracking.registrosSeleccionados()"
          [conteos]="tanda()?.resumen ?? null"
          [total]="tracking.totalRegistrosSeleccionados()"
        />
      } @else {
        <p class="sinay-proceso__vacio">Aún no hay renglones registrados para esta tanda.</p>
      }
    </intelishs-panel-proceso>
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeguimientoTandaComponent {
  protected readonly tracking = inject(ComplementosBulkTrackingService);

  protected readonly tanda = this.tracking.tandaSeleccionada;

  protected readonly estado = computed(() => {
    const tanda = this.tanda();
    return tanda ? ESTADO[tanda.estado] : null;
  });

  protected readonly hechos = computed(() => cerradosDe(this.tanda()));

  protected readonly anteriores = computed<ProcesoAnterior[]>(() =>
    this.tracking.otrasTandas().map((t) => ({
      id: t.groupId,
      fecha: t.iniciadaEn,
      resumen: `${t.resumen?.completados ?? 0} ok · ${t.resumen?.conError ?? 0} error · ${t.enviados} enviados`,
    })),
  );
}
